/**
 * Create Job Page
 * Multi-step form for Studios to create job listings
 * Fixes: multi-select shooting styles, step descriptors, icon colors,
 * venue name, no number spinners, flexible pay, robust error handling
 */
import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { NavBar } from "@/components/NavBar";
import { ArrowLeft, ArrowRight, Loader2, Calendar, Clock, MapPin, DollarSign, FileText, Check, Users, ScrollText, Briefcase, User, Tag } from "lucide-react";

import { GENRES, HIREABLE_ROLES, JOB_REQUIRED_SKILLS } from "@/lib/profileOptions";

const STEPS = [
  { num: 1, label: "Job Details" },
  { num: 2, label: "Date & Location" },
  { num: 3, label: "Pay" },
  { num: 4, label: "Review" },
];

export default function CreateJobPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  // What posting will cost, said before the form rather than after it. The
  // price used to appear for the first time on the candidates screen, once
  // the whole wizard was already filled in.
  const [covered, setCovered] = useState(null);
  const [firstFree, setFirstFree] = useState(false);

  // Form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [roleNeeded, setRoleNeeded] = useState("");
  const [requiresLead, setRequiresLead] = useState(false);
  const [genre, setGenre] = useState("");
  const [requiredSkills, setRequiredSkills] = useState([]);
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [hours, setHours] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [venueName, setVenueName] = useState("");
  const [venueAddress, setVenueAddress] = useState("");
  const [paymentType, setPaymentType] = useState("hourly");
  const [hourlyRate, setHourlyRate] = useState("");
  const [flatRate, setFlatRate] = useState("");
  const [rateMin, setRateMin] = useState("");
  const [rateMax, setRateMax] = useState("");
  const [isFlexible, setIsFlexible] = useState(false);
  const [notes, setNotes] = useState("");
  const [matchCount, setMatchCount] = useState(null);
  const [matchLoading, setMatchLoading] = useState(false);
  const [contractTemplates, setContractTemplates] = useState([]);
  const [selectedContract, setSelectedContract] = useState("");

  // Fetch match preview when reaching step 4
  const fetchMatchPreview = useCallback(async () => {
    if (!genre || !roleNeeded) return;
    setMatchLoading(true);
    try {
      const params = new URLSearchParams({ genre, role_needed: roleNeeded });
      if (city) params.set("city", city);
      const res = await fetch(`${API}/jobs/preview-matches?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setMatchCount(data.match_count);
      }
    } catch { /* silent */ }
    finally { setMatchLoading(false); }
  }, [genre, roleNeeded, city, token]);

  useEffect(() => {
    if (step === 4) {
      fetchMatchPreview();
      fetchContractTemplates();
    }
  }, [step, fetchMatchPreview]);

  const fetchContractTemplates = async () => {
    try {
      const res = await fetch(`${API}/contracts/templates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setContractTemplates(data.templates);
      }
    } catch {}
  };

  useEffect(function () {
    let cancelled = false;
    (async function () {
      try {
        const res = await fetch(`${API}/subscriptions/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok && !cancelled) {
          const d = await res.json();
          setCovered(Boolean(d.can_post_jobs));
          setFirstFree(Boolean(d.first_listing_free));
        }
      } catch {
        /* the notice just stays hidden */
      }
    })();
    return function () { cancelled = true; };
  }, [token]);

  // Check if user is a studio
  if (user && !user.roles?.includes("studio")) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-4">
        <Card className="bg-white/[0.03] border-white/10 p-8 max-w-md text-center">
          <h1 className="text-2xl font-bold mb-4">Studio Access Required</h1>
          <p className="text-white/60 mb-6">
            You need to enable the "Hire Talent" role to post jobs.
          </p>
          <Link to="/profile/edit">
            <Button className="bg-white text-black hover:bg-gray-200">
              Update Profile
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  const toggleSkill = (value) => {
    setRequiredSkills((prev) =>
      prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value]
    );
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      let locationData = { city, state, zip_code: zipCode, coordinates: [] };

      if (city) {
        try {
          const geoRes = await fetch(
            `${API}/geocode?address=${encodeURIComponent(`${city} ${state} ${zipCode}`)}`,
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          );
          if (geoRes.ok) {
            const geo = await geoRes.json();
            if (geo.lat && geo.lng) {
              locationData.coordinates = [geo.lng, geo.lat];
            }
          }
        } catch {
          // Geocoding is optional – continue without coordinates
        }
      }

      const jobData = {
        title,
        description,
        role_needed: roleNeeded,
        requires_lead: requiresLead,
        genre,
        date: date,  // Keep as simple date string without timezone conversion
        start_time: startTime || null,
        end_time: endTime || null,
        timezone: timezone,
        hours: hours ? parseFloat(hours) : null,
        location: locationData,
        venue_name: venueName || null,
        venue_address: venueAddress || null,
        payment_type: isFlexible ? "flexible" : paymentType,
        hourly_rate: !isFlexible && paymentType === "hourly" && hourlyRate ? parseFloat(hourlyRate) : null,
        flat_rate: !isFlexible && paymentType === "flat" && flatRate ? parseFloat(flatRate) : null,
        rate_min: isFlexible && rateMin ? parseFloat(rateMin) : null,
        rate_max: isFlexible && rateMax ? parseFloat(rateMax) : null,
        is_flexible: isFlexible,
        notes,
        shooting_styles: [],
        required_skills: requiredSkills,
        required_gear: [],
        contract_template_id: selectedContract || null,
      };

      const response = await fetch(`${API}/jobs/enhanced`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(jobData),
      });

      const data = await response.json().catch(() => null);

      if (response.ok && data) {
        // "Created successfully" was told to everyone, including the studios
        // whose listing was sitting unpublished and invisible. A studio that
        // believes it posted has no reason to go looking for a payment step.
        if (data.status === "open") {
          toast.success("Your job is live!");
        } else {
          toast.success("Job saved — one more step to publish it");
        }
        navigate(`/jobs/${data.job_id}/candidates`);
      } else {
        toast.error(data?.detail || "Failed to create job. Please try again.");
      }
    } catch (error) {
      console.error("Create job error:", error);
      toast.error("Network error. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return title && roleNeeded && genre;
      case 2:
        return date && city;
      case 3:
        if (isFlexible) return true;
        return (paymentType === "hourly" && hourlyRate) || (paymentType === "flat" && flatRate);
      default:
        return true;
    }
  };

  const getPaySummary = () => {
    if (isFlexible) {
      if (rateMin && rateMax) return `$${rateMin} – $${rateMax} (flexible)`;
      return "Flexible / negotiable";
    }
    if (paymentType === "hourly") return `$${hourlyRate}/hr`;
    return `$${flatRate} flat`;
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Navigation */}
      <NavBar>
        <Link to="/dashboard">
          <Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10">
            Dashboard
          </Button>
        </Link>
      </NavBar>

      <main className="pt-24 pb-16 px-4 md:px-12">
        <div className="max-w-2xl mx-auto">
          {/* Back button */}
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back</span>
          </Link>

          {/* Header */}
          <h1 className="text-2xl md:text-4xl font-bold mb-2">Post a Job</h1>
          <p className="text-white/60 mb-4">Find the perfect second shooter for your next gig</p>

          {covered !== null && (
            <div
              className="flex items-start gap-2.5 mb-8 text-sm rounded-sm border border-white/10 bg-white/[0.03] px-4 py-3"
              data-testid="listing-cost-notice"
            >
              <Tag className="w-4 h-4 text-white/40 mt-0.5 shrink-0" />
              {covered ? (
                <p className="text-white/60">
                  Posting is <span className="text-white">included</span> in your studio
                  subscription. Your job goes live as soon as you post it.
                </p>
              ) : firstFree ? (
                <p className="text-white/60">
                  Your <span className="text-white">first listing is free</span> — it goes live
                  as soon as you post it, no payment needed. After that, publishing a listing is
                  $10, or $19/month for unlimited posts.
                </p>
              ) : (
                <p className="text-white/60">
                  Publishing a listing costs <span className="text-white">$10</span>, or{" "}
                  <span className="text-white">$19/month</span> for unlimited posts. You will
                  be asked at the end — your job stays saved until then, but it is not visible
                  to second shooters until it is published.
                </p>
              )}
            </div>
          )}

          {/* Progress Steps with labels */}
          <div className="flex items-start gap-2 mb-8" data-testid="progress-steps">
            {STEPS.map((s, i) => (
              <div key={s.num} className="flex-1 flex flex-col items-center gap-1">
                <div className="flex items-center w-full">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
                      step > s.num
                        ? "bg-white text-black"
                        : step === s.num
                        ? "bg-white text-black"
                        : "bg-white/10 text-white/40"
                    }`}
                  >
                    {step > s.num ? <Check className="w-4 h-4" /> : s.num}
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-1 ${step > s.num ? "bg-white" : "bg-white/10"}`} />
                  )}
                </div>
                <span className={`text-[11px] leading-tight ${step >= s.num ? "text-white/80" : "text-white/30"}`}>
                  {s.label}
                </span>
              </div>
            ))}
          </div>

          {/* Step 1: Basic Info */}
          {step === 1 && (
            <Card className="bg-white/[0.03] border-white/10 p-6 md:p-8" data-testid="step-1-card">
              <div className="flex items-center gap-3 mb-6">
                <FileText className="w-5 h-5 text-white/60" />
                <h2 className="text-xl font-bold">Job Details</h2>
              </div>

              <div className="space-y-6">
                <div>
                  <Label className="text-sm text-white/60 uppercase tracking-wider">Job Title *</Label>
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g., Wedding Second Shooter Needed"
                    className="mt-2 bg-white/[0.05] border-white/10 focus:border-white/40 text-white h-12 rounded-sm"
                    data-testid="job-title-input"
                  />
                </div>

                <div>
                  <Label className="text-sm text-white/60 uppercase tracking-wider">Role Needed *</Label>
                  <select
                    value={roleNeeded}
                    onChange={(e) => setRoleNeeded(e.target.value)}
                    className="mt-2 w-full h-12 bg-white/[0.05] border border-white/10 rounded-sm text-white px-3"
                    data-testid="role-select"
                  >
                    <option value="">Select a role...</option>
                    {HIREABLE_ROLES.map((role) => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                  <label
                    className="flex items-center gap-2 mt-3 cursor-pointer"
                    data-testid="requires-lead-toggle"
                  >
                    <input
                      type="checkbox"
                      checked={requiresLead}
                      onChange={(e) => setRequiresLead(e.target.checked)}
                      className="rounded border-white/20 bg-white/5"
                    />
                    <span className="text-sm text-white/60">Requires primary/lead shooter experience</span>
                  </label>
                </div>

                <div>
                  <Label className="text-sm text-white/60 uppercase tracking-wider">Genre *</Label>
                  <select
                    value={genre}
                    onChange={(e) => setGenre(e.target.value)}
                    className="mt-2 w-full h-12 bg-white/[0.05] border border-white/10 rounded-sm text-white px-3"
                    data-testid="genre-select"
                  >
                    <option value="">Select a genre...</option>
                    {GENRES.map((g) => (
                      <option key={g.value} value={g.value}>
                        {g.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Required Skills */}
                <div>
                  <Label className="text-sm text-white/60 uppercase tracking-wider mb-3 block">
                    Required Skills (select all that apply)
                  </Label>
                  <div className="flex flex-wrap gap-2" data-testid="required-skills-group">
                    {JOB_REQUIRED_SKILLS.map((skill) => {
                      const selected = requiredSkills.includes(skill.value);
                      return (
                        <button
                          key={skill.value}
                          type="button"
                          onClick={() => toggleSkill(skill.value)}
                          className={`px-3 py-1.5 text-sm rounded-full border transition-all ${
                            selected
                              ? "bg-white text-black border-white font-semibold"
                              : "border-white/15 text-white/60 hover:border-white/30 hover:text-white"
                          }`}
                          data-testid={`skill-${skill.value}`}
                        >
                          {skill.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <Label className="text-sm text-white/60 uppercase tracking-wider">Description</Label>
                  <Textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Describe the job, expectations, and any special requirements..."
                    className="mt-2 bg-white/[0.05] border-white/10 focus:border-white/40 text-white min-h-[120px] rounded-sm"
                    data-testid="description-input"
                  />
                </div>
              </div>

              <div className="flex justify-end mt-8">
                <Button
                  onClick={() => setStep(2)}
                  disabled={!canProceed()}
                  className="bg-white text-black hover:bg-gray-200 rounded-sm font-bold px-8"
                  data-testid="step1-next-btn"
                >
                  Next <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </Card>
          )}

          {/* Step 2: Date & Location */}
          {step === 2 && (
            <Card className="bg-white/[0.03] border-white/10 p-6 md:p-8" data-testid="step-2-card">
              <div className="flex items-center gap-3 mb-6">
                <Calendar className="w-5 h-5 text-white/60" />
                <h2 className="text-xl font-bold">Date & Location</h2>
              </div>

              <div className="space-y-6">
                <div>
                  <Label className="text-sm text-white/60 uppercase tracking-wider">Date *</Label>
                  <div className="relative mt-2">
                    <Input
                      type="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      className="bg-white/[0.05] border-white/10 focus:border-white/40 text-white h-12 rounded-sm pr-12 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-12 [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                      data-testid="date-input"
                    />
                    <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/70 pointer-events-none" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-white/60 uppercase tracking-wider">Start Time</Label>
                    <div className="relative mt-2">
                      <Input
                        type="time"
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                        className="bg-white/[0.05] border-white/10 focus:border-white/40 text-white h-12 rounded-sm pr-12 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-12 [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                        data-testid="start-time-input"
                      />
                      <Clock className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/70 pointer-events-none" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm text-white/60 uppercase tracking-wider">End Time</Label>
                    <div className="relative mt-2">
                      <Input
                        type="time"
                        value={endTime}
                        onChange={(e) => setEndTime(e.target.value)}
                        className="bg-white/[0.05] border-white/10 focus:border-white/40 text-white h-12 rounded-sm pr-12 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-12 [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                        data-testid="end-time-input"
                      />
                      <Clock className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/70 pointer-events-none" />
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="text-sm text-white/60 uppercase tracking-wider">Timezone</Label>
                  <select 
                    value={timezone} 
                    onChange={(e) => setTimezone(e.target.value)} 
                    className="w-full mt-2 h-12 bg-white/[0.05] border border-white/10 focus:border-white/40 rounded-sm text-white px-3"
                    data-testid="timezone-select"
                  >
                    <option value="America/New_York">Eastern Time (ET)</option>
                    <option value="America/Chicago">Central Time (CT)</option>
                    <option value="America/Denver">Mountain Time (MT)</option>
                    <option value="America/Los_Angeles">Pacific Time (PT)</option>
                    <option value="America/Phoenix">Arizona (no DST)</option>
                    <option value="Pacific/Honolulu">Hawaii Time (HT)</option>
                    <option value="America/Anchorage">Alaska Time (AKT)</option>
                    <option value="Europe/London">London (GMT/BST)</option>
                    <option value="Europe/Paris">Central European (CET)</option>
                    <option value="Asia/Tokyo">Japan (JST)</option>
                    <option value="Australia/Sydney">Sydney (AEST)</option>
                  </select>
                </div>

                <div>
                  <Label className="text-sm text-white/60 uppercase tracking-wider">Estimated Hours</Label>
                  <Input
                    type="number"
                    value={hours}
                    onChange={(e) => setHours(e.target.value)}
                    placeholder="e.g., 8"
                    min="1"
                    max="24"
                    className="mt-2 bg-white/[0.05] border-white/10 focus:border-white/40 text-white h-12 rounded-sm"
                    data-testid="hours-input"
                  />
                </div>

                <div className="pt-4 border-t border-white/10">
                  <div className="flex items-center gap-2 mb-4">
                    <MapPin className="w-5 h-5 text-white/60" />
                    <Label className="text-sm text-white/60 uppercase tracking-wider">Location</Label>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm text-white/40">Venue Name</Label>
                      <Input
                        value={venueName}
                        onChange={(e) => setVenueName(e.target.value)}
                        placeholder="e.g., The Grand Ballroom"
                        className="mt-1 bg-white/[0.05] border-white/10 focus:border-white/40 text-white h-12 rounded-sm"
                        data-testid="venue-name-input"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-white/40">Venue Address</Label>
                      <Input
                        value={venueAddress}
                        onChange={(e) => setVenueAddress(e.target.value)}
                        placeholder="e.g., 123 Main St, Suite 100"
                        className="mt-1 bg-white/[0.05] border-white/10 focus:border-white/40 text-white h-12 rounded-sm"
                        data-testid="venue-address-input"
                      />
                      <p className="text-white/30 text-xs mt-1">Full address is only shared after a candidate is hired.</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="md:col-span-2">
                        <Label className="text-sm text-white/40">City *</Label>
                        <Input
                          value={city}
                          onChange={(e) => setCity(e.target.value)}
                          placeholder="e.g., Los Angeles"
                          className="mt-1 bg-white/[0.05] border-white/10 focus:border-white/40 text-white h-12 rounded-sm"
                          data-testid="city-input"
                        />
                      </div>
                      <div>
                        <Label className="text-sm text-white/40">State</Label>
                        <Input
                          value={state}
                          onChange={(e) => setState(e.target.value)}
                          placeholder="CA"
                          className="mt-1 bg-white/[0.05] border-white/10 focus:border-white/40 text-white h-12 rounded-sm"
                          data-testid="state-input"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm text-white/40">ZIP Code</Label>
                      <Input
                        value={zipCode}
                        onChange={(e) => setZipCode(e.target.value)}
                        placeholder="90001"
                        className="mt-1 bg-white/[0.05] border-white/10 focus:border-white/40 text-white h-12 rounded-sm w-32"
                        data-testid="zip-input"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-between mt-8">
                <Button
                  onClick={() => setStep(1)}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10 rounded-sm px-8"
                  data-testid="step2-back-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
                <Button
                  onClick={() => setStep(3)}
                  disabled={!canProceed()}
                  className="bg-white text-black hover:bg-gray-200 rounded-sm font-bold px-8"
                  data-testid="step2-next-btn"
                >
                  Next <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </Card>
          )}

          {/* Step 3: Payment */}
          {step === 3 && (
            <Card className="bg-white/[0.03] border-white/10 p-6 md:p-8" data-testid="step-3-card">
              <div className="flex items-center gap-3 mb-6">
                <DollarSign className="w-5 h-5 text-white/60" />
                <h2 className="text-xl font-bold">Payment Details</h2>
              </div>

              <div className="space-y-6">
                {/* Flexible toggle */}
                <label
                  className="flex items-center gap-3 cursor-pointer group"
                  data-testid="flexible-toggle"
                  onClick={() => setIsFlexible(!isFlexible)}
                >
                  <div
                    className={`w-5 h-5 rounded border flex items-center justify-center transition-all ${
                      isFlexible ? "bg-white border-white" : "border-white/20 group-hover:border-white/40"
                    }`}
                  >
                    {isFlexible && <Check className="w-3.5 h-3.5 text-black" />}
                  </div>
                  <div>
                    <span className="font-semibold text-sm">Flexible / Negotiable</span>
                    <p className="text-white/40 text-xs">Rate depends on experience or is negotiable</p>
                  </div>
                </label>

                {isFlexible ? (
                  <div className="space-y-4 p-4 bg-white/5 rounded-sm border border-white/10">
                    <p className="text-white/50 text-sm">Optionally specify a rate range:</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm text-white/40">Min Rate</Label>
                        <div className="relative mt-1">
                          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30">$</span>
                          <Input
                            type="number"
                            value={rateMin}
                            onChange={(e) => setRateMin(e.target.value)}
                            placeholder="50"
                            min="0"
                            className="bg-white/[0.05] border-white/10 focus:border-white/40 text-white h-12 pl-8 rounded-sm"
                            data-testid="rate-min-input"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-sm text-white/40">Max Rate</Label>
                        <div className="relative mt-1">
                          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30">$</span>
                          <Input
                            type="number"
                            value={rateMax}
                            onChange={(e) => setRateMax(e.target.value)}
                            placeholder="100"
                            min="0"
                            className="bg-white/[0.05] border-white/10 focus:border-white/40 text-white h-12 pl-8 rounded-sm"
                            data-testid="rate-max-input"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div>
                      <Label className="text-sm text-white/60 uppercase tracking-wider mb-3 block">
                        Payment Type
                      </Label>
                      <div className="grid grid-cols-2 gap-4">
                        <div
                          onClick={() => setPaymentType("hourly")}
                          className={`p-4 border rounded-sm cursor-pointer transition-all ${
                            paymentType === "hourly"
                              ? "border-white/40 bg-white/5"
                              : "border-white/10 hover:border-white/20"
                          }`}
                          data-testid="payment-type-hourly"
                        >
                          <div className="font-semibold mb-1">Hourly Rate</div>
                          <div className="text-white/50 text-sm">Pay by the hour</div>
                        </div>
                        <div
                          onClick={() => setPaymentType("flat")}
                          className={`p-4 border rounded-sm cursor-pointer transition-all ${
                            paymentType === "flat"
                              ? "border-white/40 bg-white/5"
                              : "border-white/10 hover:border-white/20"
                          }`}
                          data-testid="payment-type-flat"
                        >
                          <div className="font-semibold mb-1">Flat Rate</div>
                          <div className="text-white/50 text-sm">One price for the job</div>
                        </div>
                      </div>
                    </div>

                    {paymentType === "hourly" ? (
                      <div>
                        <Label className="text-sm text-white/60 uppercase tracking-wider">Hourly Rate *</Label>
                        <div className="relative mt-2">
                          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30">$</span>
                          <Input
                            type="number"
                            value={hourlyRate}
                            onChange={(e) => setHourlyRate(e.target.value)}
                            placeholder="75"
                            min="1"
                            className="bg-white/[0.05] border-white/10 focus:border-white/40 text-white h-12 pl-8 rounded-sm"
                            data-testid="hourly-rate-input"
                          />
                          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30">/hr</span>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <Label className="text-sm text-white/60 uppercase tracking-wider">Total Payment *</Label>
                        <div className="relative mt-2">
                          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30">$</span>
                          <Input
                            type="number"
                            value={flatRate}
                            onChange={(e) => setFlatRate(e.target.value)}
                            placeholder="600"
                            min="1"
                            className="bg-white/[0.05] border-white/10 focus:border-white/40 text-white h-12 pl-8 rounded-sm"
                            data-testid="flat-rate-input"
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="flex justify-between mt-8">
                <Button
                  onClick={() => setStep(2)}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10 rounded-sm px-8"
                  data-testid="step3-back-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
                <Button
                  onClick={() => setStep(4)}
                  disabled={!canProceed()}
                  className="bg-white text-black hover:bg-gray-200 rounded-sm font-bold px-8"
                  data-testid="step3-next-btn"
                >
                  Next <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </Card>
          )}

          {/* Step 4: Review & Submit */}
          {step === 4 && (
            <Card className="bg-white/[0.03] border-white/10 p-6 md:p-8" data-testid="step-4-card">
              <h2 className="text-xl font-bold mb-6">Review & Post</h2>

              {/* Quick Match Preview */}
              <div
                className={`flex items-center gap-3 p-4 rounded-sm mb-6 border ${
                  matchCount === null
                    ? "bg-white/5 border-white/10"
                    : matchCount > 0
                    ? "bg-[rgba(255,107,107,0.1)] border-[rgba(255,107,107,0.2)]"
                    : "bg-amber-500/10 border-amber-500/20"
                }`}
                data-testid="match-preview"
              >
                <Users className={`w-5 h-5 shrink-0 ${matchCount > 0 ? "text-[#3498DB]" : matchCount === 0 ? "text-amber-400" : "text-white/40"}`} />
                {matchLoading ? (
                  <span className="text-white/50 text-sm">Checking available candidates...</span>
                ) : matchCount === null ? (
                  <span className="text-white/50 text-sm">Unable to check matches</span>
                ) : matchCount > 0 ? (
                  <div>
                    <span className="font-bold text-[#3498DB]">{matchCount} candidate{matchCount !== 1 ? "s" : ""}</span>
                    <span className="text-white/60 text-sm ml-1">match this job in your area</span>
                  </div>
                ) : (
                  <div>
                    <span className="font-bold text-amber-400">No matches yet</span>
                    <span className="text-white/50 text-sm ml-1">
                      — your listing will be free until candidates sign up. We'll notify you when someone matches.
                    </span>
                  </div>
                )}
              </div>

              <div className="space-y-4 mb-6">
                <div className="p-4 bg-white/[0.05] rounded-sm">
                  <div className="text-white/50 text-sm mb-1">Job Title</div>
                  <div className="font-semibold" data-testid="review-title">{title}</div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-white/[0.05] rounded-sm">
                    <div className="text-white/50 text-sm mb-1">Role</div>
                    <div>{HIREABLE_ROLES.find((r) => r.value === roleNeeded)?.label}</div>
                  </div>
                  <div className="p-4 bg-white/[0.05] rounded-sm">
                    <div className="text-white/50 text-sm mb-1">Genre</div>
                    <div>{GENRES.find((g) => g.value === genre)?.label}</div>
                  </div>
                </div>
                {requiredSkills.length > 0 && (
                  <div className="p-4 bg-white/[0.05] rounded-sm">
                    <div className="text-white/50 text-sm mb-1">Required Skills</div>
                    <div className="flex flex-wrap gap-1.5">
                      {requiredSkills.map((s) => (
                        <span key={s} className="text-xs bg-white/10 px-2 py-0.5 rounded-full">
                          {JOB_REQUIRED_SKILLS.find((sk) => sk.value === s)?.label || s}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-white/[0.05] rounded-sm">
                    <div className="text-white/50 text-sm mb-1">Date & Time</div>
                    <div>
                      {date ? new Date(date + "T00:00:00").toLocaleDateString() : "—"}
                      {(startTime || endTime) && (
                        <span className="text-white/70 ml-2">
                          {startTime || "?"} – {endTime || "?"}
                        </span>
                      )}
                      {timezone && <span className="text-white/40 text-sm ml-2">({timezone.split("/").pop().replace("_", " ")})</span>}
                    </div>
                  </div>
                  <div className="p-4 bg-white/[0.05] rounded-sm">
                    <div className="text-white/50 text-sm mb-1">Location</div>
                    <div>
                      {venueName && <span className="block text-sm">{venueName}</span>}
                      {city}{state ? `, ${state}` : ""}
                    </div>
                  </div>
                </div>
                <div className="p-4 bg-white/[0.05] rounded-sm">
                  <div className="text-white/50 text-sm mb-1">Payment</div>
                  <div className="text-xl font-bold" data-testid="review-payment">{getPaySummary()}</div>
                </div>
              </div>

              <div>
                <Label className="text-sm text-white/60 uppercase tracking-wider">Additional Notes</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any additional information for candidates..."
                  className="mt-2 bg-white/[0.05] border-white/10 focus:border-white/40 text-white min-h-[100px] rounded-sm"
                  data-testid="notes-input"
                />
              </div>

              {/* Contract Template Selection */}
              {contractTemplates.length > 0 && (
                <div className="mt-6 p-5 bg-white/[0.03] border border-white/10 rounded-sm" data-testid="contract-selection-section">
                  <div className="flex items-center gap-2 mb-3">
                    <ScrollText className="w-5 h-5 text-[#FF6B6B]" />
                    <Label className="text-sm font-semibold uppercase tracking-wider">Contract for this Job</Label>
                  </div>
                  <p className="text-white/40 text-xs mb-3">
                    Select a contract template that candidates will be asked to sign when hired for this job.
                  </p>
                  <select
                    value={selectedContract}
                    onChange={(e) => setSelectedContract(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 text-white rounded-md px-3 py-2.5 text-sm"
                    data-testid="job-contract-select"
                  >
                    <option value="">No contract required</option>
                    {contractTemplates.map((t) => (
                      <option key={t.template_id} value={t.template_id}>
                        {t.title} ({t.contract_type === "per_job" ? "Per Job" : "Per Person"})
                      </option>
                    ))}
                  </select>
                  {selectedContract && (
                    <div className="mt-2 flex items-center gap-2 text-xs text-white/50">
                      {contractTemplates.find(t => t.template_id === selectedContract)?.contract_type === "per_job" ? (
                        <><Briefcase className="w-3.5 h-3.5 text-[#3498DB]" /> A new contract will be created for each hire on this job</>
                      ) : (
                        <><User className="w-3.5 h-3.5 text-[#3498DB]" /> Candidates who already signed this contract won't need to sign again</>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-between mt-8">
                <Button
                  onClick={() => setStep(3)}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10 rounded-sm px-8"
                  data-testid="step4-back-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="bg-white text-black hover:bg-gray-200 rounded-sm font-bold px-8"
                  data-testid="submit-job-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    "Post Job"
                  )}
                </Button>
              </div>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
